/**
 * Client-safe structured logger for StellarTrail.
 *
 * - Development: passes through to console with structured context.
 * - Production: suppresses debug/info; warn/error still log to console
 *   so they appear in error-tracking tools (Sentry, etc.).
 */

const isDev = import.meta.env?.DEV ?? (typeof process !== 'undefined' && process.env.NODE_ENV !== 'production');

type LogPayload = Record<string, unknown> | unknown;

function formatMsg(msg: string, ...args: unknown[]): [string, ...unknown[]] {
  if (args.length > 0) {
    return [msg, ...args];
  }
  return [msg];
}

/** Check if an error is an AbortError (from React StrictMode unmount or navigation).
 *  Matches both raw DOMException AbortErrors and errors wrapped by repositories. */
export function isAbortError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  if (err instanceof Error && err.message.includes('signal is aborted')) return true;
  return false;
}

export const clientLogger = {
  debug(msg: string, ...args: unknown[]): void {
    if (isDev) {
      console.debug(...formatMsg(msg, ...args));
    }
  },

  info(msg: string, ...args: unknown[]): void {
    if (isDev) {
      console.log(...formatMsg(msg, ...args));
    }
  },

  warn(msg: string, ...args: unknown[]): void {
    if (args.some(isAbortError)) return;
    console.warn(...formatMsg(msg, ...args));
  },

  error(msg: string, ...args: unknown[]): void {
    if (args.some(isAbortError)) return;
    console.error(...formatMsg(msg, ...args));
  },
};
